import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Sidebar from "../components/shared/Sidebar";
import Navbar from "../components/shared/Navbar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { createMetaOAuthConnector } from "../lib/metaOAuth";
import {
  MessageCircle, Instagram, Facebook, CheckCircle2, XCircle, AlertCircle,
  RefreshCw, Wifi, WifiOff, Copy, ExternalLink, ChevronDown, ChevronUp,
  ShieldCheck, Loader2, X, ArrowRight, Clock, Zap, Globe, Mail,
  Smartphone, Activity, PlugZap, Lock, LogIn,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ExternalIntegrationsPanel from "../components/integrations/ExternalIntegrationsPanel";
import { FEATURE_KEYS, hasAnyFeature, hasFeature, isSuperAdmin as isSuperAdminUser } from "../lib/features";

//  Constantes

const WEBHOOK_BASE = "https://api.clivercrm.com.br";

const PROVIDERS = {
  whatsapp: {
    key: "whatsapp",
    title: "WhatsApp Oficial",
    subtitle: "Cloud API Meta",
    description: "Envie e receba mensagens com números oficiais do WhatsApp Business. Templates aprovados, alta entregabilidade.",
    icon: MessageCircle,
    color: "#25D366",
    bg: "bg-[#25D366]/10",
    border: "border-[#25D366]/20",
    glow: "shadow-[0_0_24px_rgba(37,211,102,0.12)]",
    webhookUrl: `${WEBHOOK_BASE}/api/whatsapp/webhook`,
    benefits: ["Templates oficiais Meta", "Alta entregabilidade", "Sem bloqueios", "API v20.0"],
  },
  instagram: {
    key: "instagram",
    title: "Instagram Direct",
    subtitle: "Mensagens diretas",
    description: "Centralize DMs do Instagram profissional conectado à sua Página do Facebook no CRM.",
    icon: Instagram,
    color: "#E1306C",
    bg: "bg-[#E1306C]/10",
    border: "border-[#E1306C]/20",
    glow: "shadow-[0_0_24px_rgba(225,48,108,0.10)]",
    webhookUrl: `${WEBHOOK_BASE}/api/webhooks/instagram`,
    benefits: ["DMs em tempo real", "Perfis profissionais", "Integrado ao Facebook", "Sem app terceiros"],
  },
  facebook: {
    key: "facebook",
    title: "Facebook Messenger",
    subtitle: "Páginas do Facebook",
    description: "Gerencie mensagens da sua Página do Facebook diretamente no CRM. Resposta rápida em um só lugar.",
    icon: Facebook,
    color: "#1877F2",
    bg: "bg-[#1877F2]/10",
    border: "border-[#1877F2]/20",
    glow: "shadow-[0_0_24px_rgba(24,119,242,0.10)]",
    webhookUrl: `${WEBHOOK_BASE}/api/webhooks/facebook`,
    benefits: ["Gestão de Páginas", "Respostas automáticas", "Integrado ao Business Manager", "Alta conversão"],
  },
};

const PROVIDER_FEATURES = {
  whatsapp: FEATURE_KEYS.WHATSAPP_META,
  instagram: FEATURE_KEYS.INSTAGRAM_DIRECT,
  facebook: FEATURE_KEYS.FACEBOOK_MESSENGER,
};

//  Status helpers

const STATUS_META = {
  connected: { label: "Conectado", color: "text-[#25D366]", bg: "bg-[#25D366]/15", dot: "bg-[#25D366]", icon: CheckCircle2 },
  connected_mock: { label: "Dev Mode", color: "text-blue-400", bg: "bg-blue-400/10", dot: "bg-blue-400", icon: CheckCircle2 },
  connecting: { label: "Conectando...", color: "text-yellow-400", bg: "bg-yellow-400/10", dot: "bg-yellow-400 animate-pulse", icon: Loader2 },
  error: { label: "Erro", color: "text-red-400", bg: "bg-red-400/10", dot: "bg-red-400", icon: XCircle },
  disconnected: { label: "Desconectado", color: "text-slate-500", bg: "bg-slate-700/20", dot: "bg-slate-600", icon: WifiOff },
};

function getStatus(s) { return STATUS_META[s] ?? STATUS_META.disconnected; }

function isConnected(s) { return s === "connected" || s === "connected_mock"; }

//  Utilidades visuais

function StatusPill({ status }) {
  const cfg = getStatus(status);
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-xl bg-slate-800/60 ${className}`} />;
}

//  Log timeline

function LogTimeline({ logs = [] }) {
  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      if (diff < 60000) return "agora";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m atrás`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h atrás`;
      return d.toLocaleDateString("pt-BR");
    } catch { return "-"; }
  };

  const levelCfg = {
    info:    { color: "text-slate-400", dot: "bg-slate-600", icon: "" },
    success: { color: "text-[#25D366]", dot: "bg-[#25D366]", icon: "" },
    warning: { color: "text-amber-400", dot: "bg-amber-400", icon: "!" },
    error:   { color: "text-red-400",   dot: "bg-red-400",   icon: "" },
  };

  if (!logs.length) {
    return <p className="text-[11px] text-slate-600 italic text-center py-2">Nenhuma atividade registrada</p>;
  }

  return (
    <div className="relative space-y-2 pl-4 max-h-44 overflow-y-auto pr-1">
      {/* Linha vertical */}
      <div className="absolute left-1.5 top-0 bottom-0 w-px bg-slate-800" />
      {[...logs].reverse().slice(0, 15).map((log, i) => {
        const cfg = levelCfg[log.level] ?? levelCfg.info;
        return (
          <div key={i} className="flex items-start gap-2.5">
            <span className={`shrink-0 w-3 h-3 rounded-full mt-0.5 border-2 border-slate-900 ${cfg.dot}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-[11px] leading-tight ${cfg.color}`}>{log.message}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">{fmt(log.timestamp)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

//  Seção avançada colapsável

function AdvancedPanel({ webhookUrl, statusData, children }) {
  const [open, setOpen] = useState(false);
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  return (
    <div className="border-t border-slate-800 mt-4">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Lock className="w-3 h-3 text-slate-600" />
          <span className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold">
            Configuração Avançada
          </span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-600" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-600" />}
      </button>
      {open && (
        <div className="pb-4 space-y-3">
          {/* Webhook URL */}
          {webhookUrl && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">URL do Webhook</p>
              <div className="flex items-center gap-1.5">
                <Input readOnly value={webhookUrl} className="flex-1 bg-slate-950 border-slate-800 text-[11px] font-mono text-slate-400 h-7" />
                <button onClick={() => copy(webhookUrl, "URL")} className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-100 rounded-lg transition">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
          {/* Token mascarado */}
          {statusData?.token_masked && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">Token (mascarado)</p>
              <Input readOnly value={statusData.token_masked} className="bg-slate-950 border-slate-800 text-[11px] font-mono text-slate-500 h-7" />
            </div>
          )}
          {/* IDs tcnicos */}
          {statusData?.waba_id && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">WABA ID</p>
              <div className="flex items-center gap-1.5">
                <Input readOnly value={statusData.waba_id} className="flex-1 bg-slate-950 border-slate-800 text-[11px] font-mono text-slate-400 h-7" />
                <button onClick={() => copy(statusData.waba_id, "WABA ID")} className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-100 rounded-lg transition">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
          {statusData?.phone_number_id && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">Phone Number ID</p>
              <div className="flex items-center gap-1.5">
                <Input readOnly value={statusData.phone_number_id} className="flex-1 bg-slate-950 border-slate-800 text-[11px] font-mono text-slate-400 h-7" />
                <button onClick={() => copy(statusData.phone_number_id, "Phone Number ID")} className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-100 rounded-lg transition">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
          {statusData?.page_id && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">Page ID</p>
              <div className="flex items-center gap-1.5">
                <Input readOnly value={statusData.page_id} className="flex-1 bg-slate-950 border-slate-800 text-[11px] font-mono text-slate-400 h-7" />
                <button onClick={() => copy(statusData.page_id, "Page ID")} className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-100 rounded-lg transition">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

//  Modal de Conexão OAuth

function OAuthModal({ open, onClose, providerKey, onConfirm, connecting }) {
  const cfg = PROVIDERS[providerKey];
  if (!open || !cfg) return null;

  const Icon = cfg.icon;

  const steps = {
    whatsapp: ["Login com conta Meta Business", "Selecionar Business Manager e WABA", "Escolher nmero oficial de telefone", "Autorizar o Cliver CRM"],
    instagram: ["Login com conta Meta", "Selecionar Página do Facebook vinculada", "Autorizar acesso ao Instagram profissional"],
    facebook: ["Login com conta Meta", "Selecionar Página do Facebook", "Autorizar gerenciamento de mensagens"],
  };

  const permissions = {
    whatsapp: ["whatsapp_business_management", "whatsapp_business_messaging", "business_management"],
    instagram: ["instagram_basic", "instagram_manage_messages", "pages_show_list", "pages_read_engagement", "business_management"],
    facebook: ["pages_messaging", "pages_show_list", "pages_read_engagement", "business_management", "pages_manage_metadata"],
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={!connecting ? onClose : undefined} />

      <div className="relative bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Gradient top bar */}
        <div className="h-1" style={{ background: `linear-gradient(90deg, ${cfg.color}60, ${cfg.color})` }} />

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${cfg.color}18` }}>
              <Icon className="w-5 h-5" style={{ color: cfg.color }} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>
                Conectar {cfg.title}
              </h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <ShieldCheck className="w-3 h-3 text-blue-400" />
                <span className="text-[10px] text-blue-400 font-medium">Conexo segura via Meta for Developers</span>
              </div>
            </div>
          </div>
          {!connecting && (
            <button onClick={onClose} className="text-slate-500 hover:text-slate-200 transition">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="px-6 py-5 space-y-5">
          {connecting ? (
            /* Estado: aguardando autorizao */
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${cfg.color}15` }}>
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: cfg.color }} />
                </div>
                {/* Glow animado */}
                <div
                  className="absolute inset-0 rounded-2xl animate-ping opacity-20"
                  style={{ background: cfg.color }}
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-100 mb-1">Aguardando autorizao</p>
                <p className="text-xs text-slate-400 max-w-xs">
                  Complete a autorizao na janela da Meta que foi aberta. Retornaremos automaticamente aps a conexo.
                </p>
              </div>
              <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-300 underline mt-2">
                Cancelar
              </button>
            </div>
          ) : (
            <>
              {/* Descrio */}
              <p className="text-sm text-slate-300 leading-relaxed">{cfg.description}</p>

              {/* Passos */}
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">O que vai acontecer:</p>
                <div className="space-y-2">
                  {(steps[providerKey] || []).map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: `${cfg.color}20` }}>
                        <span className="text-[10px] font-bold" style={{ color: cfg.color }}>{i + 1}</span>
                      </div>
                      <span className="text-xs text-slate-400">{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Permissões */}
              <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Permissões solicitadas:</p>
                <div className="flex flex-wrap gap-1.5">
                  {(permissions[providerKey] || []).map(p => (
                    <code key={p} className="text-[10px] bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-slate-400 font-mono">
                      {p}
                    </code>
                  ))}
                </div>
              </div>

              {/* Garantia de segurança */}
              <div className="flex items-start gap-2.5 bg-[#25D366]/5 border border-[#25D366]/15 rounded-xl px-4 py-3">
                <ShieldCheck className="w-4 h-4 text-[#25D366] shrink-0 mt-0.5" />
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  Seus tokens so criptografados e nunca expostos. Voc pode desconectar a qualquer momento sem perder dados.
                </p>
              </div>

              {/* Boto */}
              <Button
                onClick={onConfirm}
                className="w-full h-11 font-semibold text-sm text-white"
                style={{ background: "#1877F2" }}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Continuar com Meta
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

//  Card de integração principal

function IntegrationCard({ providerKey, statusData, loading, onConnect, onDisconnect, onRefresh, testingWebhook, onTestWebhook }) {
  const cfg = PROVIDERS[providerKey];
  const Icon = cfg.icon;
  const status = statusData?.status ?? "disconnected";
  const connected = isConnected(status);
  const [showLogs, setShowLogs] = useState(false);
  const logs = statusData?.logs || [];

  const accountInfo = {
    whatsapp: statusData?.phone_number ? `${statusData.phone_number}` : statusData?.waba_name || null,
    instagram: statusData?.instagram_username ? `@${statusData.instagram_username}` : null,
    facebook: statusData?.page_name || null,
  }[providerKey];

  if (loading) {
    return (
      <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Skeleton className="w-11 h-11" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  return (
    <div className={`relative bg-slate-900/60 border rounded-2xl p-5 transition-all duration-300 group ${
      connected
        ? `${cfg.border} ${cfg.glow} hover:shadow-[0_0_40px_rgba(37,211,102,0.15)]`
        : "border-slate-800 hover:border-slate-700"
    }`}>
      {/* Glow de fundo quando conectado */}
      {connected && (
        <div
          className="absolute inset-0 rounded-2xl opacity-[0.04] pointer-events-none"
          style={{ background: `radial-gradient(ellipse at top left, ${cfg.color}, transparent 70%)` }}
        />
      )}

      {/*  Header  */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
            <Icon className="w-5 h-5" style={{ color: cfg.color }} />
            {connected && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#25D366] border-2 border-slate-900" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>
                {cfg.title}
              </h3>
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-blue-400 bg-blue-400/10 border border-blue-400/15 rounded-full px-1.5 py-0.5">
                <ShieldCheck className="w-2 h-2" /> Meta Oficial
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">{cfg.subtitle}</p>
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      {/*  Conta conectada  */}
      {connected && accountInfo && (
        <div className="flex items-center gap-2 bg-[#25D366]/8 border border-[#25D366]/15 rounded-xl px-3 py-2.5 mb-4">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#25D366] shrink-0" />
          <span className="text-xs text-slate-200 font-medium">{accountInfo}</span>
          {statusData?.connected_at && (
            <span className="text-[10px] text-slate-600 ml-auto">
              {new Date(statusData.connected_at).toLocaleDateString("pt-BR")}
            </span>
          )}
        </div>
      )}

      {/*  Descrio (quando desconectado)  */}
      {!connected && (
        <p className="text-xs text-slate-500 leading-relaxed mb-4">{cfg.description}</p>
      )}

      {/*  Ações  */}
      <div className="flex items-center gap-2">
        {connected ? (
          <>
            <Button
              onClick={() => onDisconnect(providerKey)}
              variant="outline"
              className="flex-1 h-9 text-xs bg-transparent border-slate-700 text-slate-400 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30 transition"
            >
              <WifiOff className="w-3.5 h-3.5 mr-1.5" />
              Desconectar
            </Button>
            <Button
              onClick={() => onConnect(providerKey)}
              variant="outline"
              className="h-9 text-xs bg-transparent border-slate-700 text-slate-400 hover:bg-slate-800 transition"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Reconectar
            </Button>
          </>
        ) : (
          <Button
            onClick={() => onConnect(providerKey)}
            className="flex-1 h-10 text-sm font-semibold text-white transition-all"
            style={{ background: cfg.color, boxShadow: `0 0 20px ${cfg.color}30` }}
          >
            <Wifi className="w-4 h-4 mr-2" />
            Conectar {cfg.title}
          </Button>
        )}

        {/* Refresh e webhook test */}
        <button onClick={onRefresh} className="p-2 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition" title="Atualizar status">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        {connected && (
          <button
            onClick={() => onTestWebhook(providerKey)}
            disabled={testingWebhook === providerKey}
            className="p-2 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition"
            title="Testar webhook"
          >
            {testingWebhook === providerKey
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Activity className="w-3.5 h-3.5" />
            }
          </button>
        )}
      </div>

      {/*  Benefcios (quando desconectado)  */}
      {!connected && (
        <div className="grid grid-cols-2 gap-1.5 mt-4">
          {cfg.benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px] text-slate-600">
              <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: cfg.color }} />
              {b}
            </div>
          ))}
        </div>
      )}

      {/*  Logs  */}
      {logs.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowLogs(v => !v)}
            className="flex items-center gap-1.5 text-[10px] text-slate-600 hover:text-slate-400 transition mb-2"
          >
            {showLogs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showLogs ? "Ocultar" : `Atividade recente (${logs.length})`}
          </button>
          {showLogs && (
            <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3">
              <LogTimeline logs={logs} />
            </div>
          )}
        </div>
      )}

      {/*  Configuração avan?ada  */}
      <AdvancedPanel webhookUrl={cfg.webhookUrl} statusData={statusData} />
    </div>
  );
}

//  Header da Central

function IntegrationsHeader({ metaStatus, loading, onRefreshAll, providerKeys = [] }) {
  const safeProviderKeys = Array.isArray(providerKeys) ? providerKeys : [];
  const counts = { connected: 0, total: safeProviderKeys.length };
  if (!loading && metaStatus) {
    safeProviderKeys.forEach(k => {
      if (isConnected(metaStatus[k]?.status)) counts.connected++;
    });
  }
  const allConnected = counts.total > 0 && counts.connected === counts.total;

  return (
    <div className="mb-8">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <PlugZap className="w-5 h-5 text-[#25D366]" />
            <h1 className="text-xl font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>
              Central de Integrações
            </h1>
          </div>
          <p className="text-sm text-slate-500">
            Conecte seus canais oficiais Meta e centralize todas as conversas no Cliver CRM.
          </p>
        </div>
        <button
          onClick={onRefreshAll}
          className="p-2 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition shrink-0"
          title="Atualizar tudo"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Status summary */}
      <div className="flex items-center gap-4 flex-wrap">
        {loading ? (
          <>
            <Skeleton className="h-7 w-44 rounded-full" />
            <Skeleton className="h-7 w-32 rounded-full" />
          </>
        ) : (
          <>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${
              allConnected
                ? "bg-[#25D366]/10 border-[#25D366]/25 text-[#25D366]"
                : counts.connected > 0
                ? "bg-amber-400/10 border-amber-400/25 text-amber-400"
                : "bg-slate-800 border-slate-700 text-slate-400"
            }`}>
              <span className={`w-2 h-2 rounded-full ${allConnected ? "bg-[#25D366]" : counts.connected > 0 ? "bg-amber-400 animate-pulse" : "bg-slate-600"}`} />
              {loading ? "..." : `${counts.connected} de ${counts.total} canais conectados`}
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-slate-800/60 border-slate-700 text-xs text-slate-400">
              <Globe className="w-3 h-3" />
              Webhooks ativos
            </div>
          </>
        )}
      </div>
    </div>
  );
}

//  Onboarding (zero canais conectados)

function OnboardingBanner({ onConnect, providers = Object.values(PROVIDERS) }) {
  return (
    <div className="mb-8 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-[#25D366]/5 border border-[#25D366]/15 rounded-2xl p-8 text-center">
      {/* Decorao de fundo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-[#25D366]/5 blur-2xl" />
        <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-blue-500/5 blur-2xl" />
      </div>

      <div className="relative">
        <div className="w-14 h-14 rounded-2xl bg-[#25D366]/15 flex items-center justify-center mx-auto mb-4">
          <PlugZap className="w-7 h-7 text-[#25D366]" />
        </div>
        <h2 className="text-lg font-bold text-slate-100 mb-2" style={{ fontFamily: "Outfit" }}>
          Conecte seus canais oficiais
        </h2>
        <p className="text-sm text-slate-400 max-w-md mx-auto mb-6 leading-relaxed">
          Centralize WhatsApp, Instagram e Facebook em um nico CRM omnichannel. Comece conectando seu primeiro canal abaixo.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {(Array.isArray(providers) ? providers : []).map(cfg => {
            const Icon = cfg.icon;
            return (
              <button
                key={cfg.key}
                onClick={() => onConnect(cfg.key)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-700 hover:border-slate-600 bg-slate-900 hover:bg-slate-800 transition text-sm text-slate-300"
              >
                <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                {cfg.title}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

//  Seo "Em breve"

const COMING_SOON = [
  { icon: Mail, label: "Email", desc: "SMTP / Gmail" },
  { icon: Smartphone, label: "SMS", desc: "Twilio / AWS SNS" },
  { icon: Globe, label: "Telegram", desc: "Bot API" },
  { icon: Zap, label: "Chatbot AI", desc: "GPT + CRM" },
];

function ComingSoonSection() {
  return (
    <div className="mt-10">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-slate-800" />
        <span className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold px-2">Em breve</span>
        <div className="flex-1 h-px bg-slate-800" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {COMING_SOON.map(({ icon: Icon, label, desc }) => (
          <div key={label} className="flex items-center gap-3 bg-slate-900/40 border border-slate-800/60 rounded-xl px-4 py-3 opacity-50 cursor-not-allowed select-none">
            <Icon className="w-4 h-4 text-slate-600 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-slate-500">{label}</p>
              <p className="text-[10px] text-slate-600">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

//  Página principal


class IntegrationSectionBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[integrations] section render failed", this.props.name, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const box = (
        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 px-5 py-4 text-sm text-amber-100">
          <p>Não foi possível carregar as integrações.</p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false })}
            className="mt-3 rounded-lg border border-amber-300/30 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-300/10"
          >
            Tentar novamente
          </button>
        </div>
      );
      if (this.props.fullPage) {
        return (
          <div className="flex h-screen bg-slate-950 overflow-hidden">
            <Sidebar />
            <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
              <Navbar title="Integrações" subtitle="Central de integrações do Cliver CRM" />
              <div className="px-6 py-6 max-w-5xl w-full mx-auto">{box}</div>
            </main>
          </div>
        );
      }
      return box;
    }

    return this.props.children;
  }
}

function IntegrationsContent() {
  const { user } = useAuth();
  const [metaStatus, setMetaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(null);
  const connectorRef = useRef(null);

  //  Permissões
  const isSuperAdmin = isSuperAdminUser(user);
  const canViewMeta = hasAnyFeature(user, [
    FEATURE_KEYS.META_OFFICIAL,
    FEATURE_KEYS.WHATSAPP_META,
    FEATURE_KEYS.INSTAGRAM_DIRECT,
    FEATURE_KEYS.FACEBOOK_MESSENGER,
  ]);
  const canViewExternal = hasAnyFeature(user, [
    FEATURE_KEYS.EXTERNAL_INTEGRATIONS,
    FEATURE_KEYS.API_KEYS,
    FEATURE_KEYS.WEBHOOKS,
    FEATURE_KEYS.API_DOCS,
  ]);
  const canViewPage = canViewMeta || canViewExternal;
  const visibleProviders = Object.keys(PROVIDERS).filter((key) => hasFeature(user, PROVIDER_FEATURES[key]));
  const enabledFeatures = useMemo(() => Object.entries(user?.features || {})
    .filter(([, enabled]) => Boolean(enabled))
    .map(([key]) => key)
    .sort(), [user?.features]);
  const enabledFeaturesKey = enabledFeatures.join("|");

  useEffect(() => {
    const allowedSections = [
      canViewMeta && "meta",
      canViewExternal && "external",
    ].filter(Boolean);

    console.info(
      "[integrations-access] role=%s tenant=%s enabled_features=%o",
      user?.role || "unknown",
      user?.tenant_id || user?.owner_id || user?.id || "unknown",
      enabledFeatures
    );
    console.info("[integrations-load] allowed_sections=%o", allowedSections);
  }, [user?.role, user?.tenant_id, user?.owner_id, user?.id, canViewMeta, canViewExternal, enabledFeatures, enabledFeaturesKey]);

  //  Carrega status Meta OAuth
  const loadStatus = useCallback(async () => {
    if (!canViewMeta) {
      console.info("[integrations-load] section=meta status=skipped");
      setMetaStatus(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/meta/status");
      setMetaStatus(data || {});
      console.info("[integrations-load] section=meta status=success");
    } catch (error) {
      if (error?.response?.status === 403) {
        console.info("[integrations-load] section=meta status=forbidden");
      } else {
        console.warn("[integrations-load] section=meta status=failed error=%s", error?.message || "unknown");
      }
      setMetaStatus({});
    } finally {
      setLoading(false);
    }
  }, [canViewMeta]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // L parmetros de callback OAuth na URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("meta_success")) {
      const provider = params.get("meta_success");
      const isMock = params.get("meta_mock") === "true";
      toast.success(isMock ? `${provider} conectado (modo desenvolvimento)` : `${provider} conectado com sucesso!`);
      loadStatus();
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("meta_error")) {
      toast.error(`Erro ao conectar: ${params.get("meta_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadStatus]);

  //  Abre modal e inicia OAuth

  const handleConnect = useCallback((providerKey) => {
    if (!PROVIDERS[providerKey] || !hasFeature(user, PROVIDER_FEATURES[providerKey])) {
      console.info("[integrations-load] section=meta-connect status=forbidden provider=%s", providerKey);
      toast.error("Recurso no liberado para este workspace.");
      return;
    }
    setActiveProvider(providerKey);
    setModalOpen(true);
    setConnecting(false);
  }, [user]);

  const handleOAuthConfirm = useCallback(async () => {
    if (!activeProvider) return;
    setConnecting(true);

    try {
      const { data } = await api.get(`/auth/meta/${activeProvider}`);

      if (data.mock) {
        // Modo dev sem APP_ID real
        toast.info(data.message || "Configure META_APP_ID no .env para OAuth real.");
        await loadStatus();
        setModalOpen(false);
        setConnecting(false);
        return;
      }

      if (!data.redirect_url) {
        throw new Error("URL de redirecionamento no recebida");
      }

      // Cria o popup manager
      connectorRef.current?.cancel();
      const connector = createMetaOAuthConnector({
        provider: activeProvider,
        pollFn: async () => {
          const { data: s } = await api.get("/auth/meta/status");
          return s;
        },
        onSuccess: async (statusData) => {
          const cfg = PROVIDERS[activeProvider];
          toast.success(`${cfg.title} conectado com sucesso!`);
          await loadStatus();
          setModalOpen(false);
          setConnecting(false);
        },
        onError: (error) => {
          const msgs = {
            popup_blocked: "Popup bloqueado. Permita popups para este site e tente novamente.",
            timeout: "Tempo limite atingido. Tente novamente.",
            oauth_failed: "Autorizao negada ou expirada.",
          };
          toast.error(msgs[error] || `Erro na conexo: ${error}`);
          setConnecting(false);
          setModalOpen(false);
        },
        onClose: () => {
          setConnecting(false);
          // Mantém modal aberto para o usuário poder tentar novamente
        },
      });

      connectorRef.current = connector;
      connector.connect(data.redirect_url);

    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar conexão com a Meta");
      setConnecting(false);
    }
  }, [activeProvider, loadStatus]);

  //  Desconectar

  const handleDisconnect = useCallback(async (providerKey) => {
    const cfg = PROVIDERS[providerKey];
    if (!cfg) return;
    if (!window.confirm(`Desconectar ${cfg.title}? O canal parará de receber mensagens.`)) return;
    try {
      await api.post("/auth/meta/disconnect", { provider: providerKey });
      toast.success(`${cfg.title} desconectado`);
      loadStatus();
    } catch {
      toast.error("Erro ao desconectar");
    }
  }, [loadStatus]);

  //  Testar webhook

  const handleTestWebhook = useCallback(async (providerKey) => {
    if (!PROVIDERS[providerKey]) return;
    setTestingWebhook(providerKey);
    try {
      const { data } = await api.post("/auth/meta/webhook/test", { provider: providerKey });
      if (data.ok) {
        toast.success(`Webhook OK - ${data.latency_ms}ms`);
      } else {
        toast.error(data.error || "Webhook retornou erro");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao testar webhook");
    } finally {
      setTestingWebhook(null);
    }
  }, []);

  //  Cleanup

  useEffect(() => {
    return () => { connectorRef.current?.cancel(); };
  }, []);

  const noneConnected = canViewMeta && visibleProviders.length > 0 && !loading && metaStatus && !visibleProviders.some(k => isConnected(metaStatus[k]?.status));

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Navbar title="Integrações" subtitle="Central de canais oficiais Meta - omnichannel enterprise" />

        <div className="px-6 py-6 max-w-5xl w-full mx-auto">
          {canViewPage ? (
            <>
              {canViewMeta && (
                <IntegrationSectionBoundary name="meta-official">
                  {/* Header com counters */}
                  <IntegrationsHeader metaStatus={metaStatus} loading={loading} onRefreshAll={loadStatus} providerKeys={visibleProviders} />

                  {/* Onboarding banner (s quando nada conectado) */}
                  {noneConnected && <OnboardingBanner onConnect={handleConnect} providers={visibleProviders.map((key) => PROVIDERS[key]).filter(Boolean)} />}

                  {/* Seo Meta Oficial */}
                  <div className="mb-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-bold text-slate-200" style={{ fontFamily: "Outfit" }}>
                      Meta Oficial
                    </span>
                    <span className="text-[10px] font-semibold text-blue-400 bg-blue-400/10 border border-blue-400/20 rounded-full px-2 py-0.5">
                      Certificado
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-slate-800" />
                </div>

                {visibleProviders.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {visibleProviders.map((key) => (
                      <IntegrationCard
                        key={key}
                        providerKey={key}
                        statusData={metaStatus?.[key]}
                        loading={loading}
                        onConnect={handleConnect}
                        onDisconnect={handleDisconnect}
                        onRefresh={loadStatus}
                        testingWebhook={testingWebhook}
                        onTestWebhook={handleTestWebhook}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-800 bg-slate-900/60 px-5 py-6 text-sm text-slate-500">
                    Nenhum canal Meta individual foi liberado para este workspace.
                  </div>
                )}
                  </div>
                </IntegrationSectionBoundary>
              )}

              {/* External integrations */}
              {canViewExternal && (
                <IntegrationSectionBoundary name="external-integrations">
                  <ExternalIntegrationsPanel canManage features={user?.features || {}} userRole={user?.role} />
                </IntegrationSectionBoundary>
              )}

              {/* Em breve */}
              {isSuperAdmin && <ComingSoonSection />}
            </>
          ) : (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 px-6 py-12 text-center">
              <Lock className="mx-auto mb-4 h-10 w-10 rounded-xl bg-slate-800 p-2 text-slate-500" />
              <h2 className="text-base font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>
                Voc ainda não possui integrações disponíveis.
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                Entre em contato com o administrador para liberar canais oficiais, API ou webhooks externos.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Future integration modal */}

      {/* Modal OAuth */}
      <OAuthModal
        open={modalOpen}
        onClose={() => { if (!connecting) { setModalOpen(false); connectorRef.current?.cancel(); setConnecting(false); } }}
        providerKey={activeProvider}
        onConfirm={handleOAuthConfirm}
        connecting={connecting}
      />
    </div>
  );
}

export default function Integrations() {
  return (
    <IntegrationSectionBoundary name="integrations-page" fullPage>
      <IntegrationsContent />
    </IntegrationSectionBoundary>
  );
}
